// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import React, {useEffect, useState} from 'react';
import uniq from 'lodash/uniq';
import isArray from 'lodash/isArray';
import {clsx} from 'clsx';

import {ChangeEvent} from 'common/components/select/types';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';
import {QueryOptionsSelection} from 'common/query-builder/conversion-query-options/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {
  getMetricEntryBehavior,
  isModifiedEntryBehavior,
  isModifiedEntryMetric,
  isSavedBehavior,
  isSavedMetric,
  isUnsavedBehavior,
  isUnsavedMetric,
} from 'common/report/insights/util/events';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {
  Action,
  CountBy,
  CountType,
  Entry,
  EntryPart,
  EntrySectionUpdates,
  FiltersOperator,
  GroupEventBehaviors,
  isGroupedEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryEventEntry,
  QueryGroupedEntry,
  QuerySuperEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  CountUsersOnceState,
  MAX_HOLDING_PROPERTY_CONSTANT,
  ProjectFeatureFlag,
  REPORT_BASICS_LINKS,
  ReportName,
  ResourceType,
} from 'common/report/constants';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {
  mapBookmarkExclusionStepToEventEntry,
  mapEventEntryToBookmarkExclusionStep,
} from 'common/report/funnels/models/format';
import {RangeSelection} from 'common/widgets/step-range-selector/types';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {DisplaySize} from 'common/components/tooltip/types';
import {getCountUsersOnceTypeFromEntrySelection} from 'common/query-builder/query-block/count-menu/util';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {MultiAttributionMenu} from 'common/widgets/multi-attribution-menu';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {MultiAttributionType} from 'common/widgets/multi-attribution-menu/types';
import {ButtonReact} from 'common/components/button';
import {Directions} from 'common/components/drop-menu/types';
// eslint-disable-next-line import/no-cycle
import {ConversionQueryOptionsReact} from 'common/query-builder/conversion-query-options';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';
import {EditControls} from 'common/query-builder/query-entry-section/components/edit-controls';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {isAnyOrderFunnel} from 'common/report/funnels/util';
import {getFunnelMetricAttributionItems} from 'common/report/insights/util/funnel-attribution';
import {getEditCapability} from 'common/report/util/saved-entities';
import {BehaviorMetricDisplay, MetricBehavior} from 'common/report/insights/util/types';
import {RowSelector} from 'common/query-builder/query-entry-section/components/selector';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {
  getMultiAttributionFromEntry,
  isSupportedMultiAttributionTypeForEntry,
} from 'common/query-builder/query-builder/util';
import {
  getMultiAttributionLabel,
  getMultiAttributionTitle,
  getUpdatedMultiAttribution,
} from 'common/query-builder/query-entry-section/util/multi-attribution';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {setEntryVisibility, toggleExpandedRow} from 'common/query-builder/query-entry-section/row-types/row-util';
import {TrackingEvent} from 'common/query-builder/query-entry-section/tracking';
import {useTranslation} from 'common/i18n/hooks';
import {Section} from 'common/components/items-menu/types';
import {CountQueryBlock} from 'common/query-builder/query-block/count-menu/count-query-block';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';
import {MenuType, MultiMetricsSectionItem} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {
  MetricUnitsButton,
  MetricUnitsSelectorDropdown,
} from 'iron/common/query-builder/chart-customization/metric-units';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {getUpsellInfo, ModalTrigger} from 'common/widgets/in-product-upsell-modal/util';
import {promptInProductUpsellModal} from 'common/widgets/in-product-upsell-modal/prompt';
import {isMetricUnitsEnabled} from 'common/query-builder/chart-customization/metric-units/util';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {CallingLocation} from 'common/query-builder/chart-customization/tracking';
import {currentProjectStore} from 'common/report/stores/project-details';
import {DataGroupSelect} from 'common/query-builder/data-group-select';
import {
  EditableState,
  EditState,
  getFunnelOrRetentionDataGroupId,
  getTrackingProps,
  handleSaveMetric,
  handleSelectSavedMetric,
  hasValidBehavior,
  saveBehavior,
} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/behavior-utils';
import {
  ConversionWindowUnit,
  FunnelOrder,
  FUNNELS_MATH_TYPES,
  MetricType,
  TopLevelMetricType,
} from 'common/types/reports/bookmark';
import {Behavior} from 'app_api/projects/behaviors/types';
import {useDataGroups} from 'common/contexts/react/data-group-context';
import {canShowMetricDataGroupSelect} from 'common/query-builder/query-entry-section/util/can-show-metric-data-group-select';

import {getStepSelectionLabel, getStepSelectionOptions} from './funnel-step-selection';
import {canSaveForPlan, getNewEntry, hasSavedFunnelBehaviors} from './utils';
// eslint-disable-next-line import/no-cycle
import {helpers} from './helpers';

export function FunnelsMetricEntryRow(props: EntryRowProps<MetricEntry>) {
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const [isCreatingInline, setIsCreatingInline] = useState<boolean>(
    props.entry.isExpanded && props.sectionProps.reportName === ReportName.Insights,
  );
  const [metricsUnitOpen, setMetricsUnitOpen] = useState(false);

  const isNewMetricMenuEnabled = useFeatureRollout(FeatureRolloutName.NewMetricMenu);
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const {t} = i18n;

  const hasMetricUnits = isMetricUnitsEnabled(currentProject.get().value);
  const hasFirstClassMetrics = currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics);
  const isFirstClassMetric = hasFirstClassMetrics && isSavedMetric(props.entry);
  const isPlaceholder = helpers.isPlaceholder(props);

  const track = (event: TrackingEvent, extraProps: Obj = {}) => {
    props.dispatchTrack(event, {
      ...props.getEntryTrackingProps(),
      ...extraProps,
    });
  };

  const getEditState = (forMetric?: boolean): EditState => {
    if (!canSaveForPlan(currentProject.get().value)) {
      return {
        showEditControls: false,
        saveDisabledMessage: t(`query-builder.funnels.cannot-save`),
      };
    }

    const isSaving = isFirstClassMetric
      ? metrics.isLoading(props.entry.id)
      : behaviors.isLoading(props.entry.behavior.id);

    let canEdit = false;
    let canSave = true;
    let isSaved = false;
    let saveDisabledMessage = ``;
    if (forMetric) {
      if (isFirstClassMetric) {
        isSaved = true;
        const metric = metrics.getOne(props.entry.id).value;
        canEdit = getEditCapability(metric).enableEdit;
        if (metric && !isModifiedEntryMetric({savedMetric: metric, entryMetric: props.entry})) {
          canSave = false;
          saveDisabledMessage = t(`query-builder.edit-controls.no-unsaved-changes`);
        }
      }
    } else if (isSavedBehavior(props.entry)) {
      isSaved = true;
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      canEdit = getEditCapability(behavior).enableEdit;
      if (behavior && !isModifiedEntryBehavior({behavior, entryBehavior: props.entry.behavior})) {
        canSave = false;
        saveDisabledMessage = t(`query-builder.edit-controls.no-unsaved-changes`);
      }
    }
    if (!hasValidBehavior(props.entry.behavior)) {
      canSave = false;
      saveDisabledMessage = t(`query-builder.edit-controls.incomplete-behavior`);
    }
    if (
      props.entry.behavior.behaviors.some(
        (behavior) => Array.isArray(behavior.behaviors) && behavior.behaviors.length > 1,
      )
    ) {
      canSave = false;
      saveDisabledMessage = t(`query-builder.funnels.event-comparison-unsupported`);
    }

    return {
      showEditControls: true,
      canEdit,
      canSave,
      isSaved,
      isSaving,
      saveDisabledMessage,
    };
  };

  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.Save: {
        const saveAsNew = isUnsavedBehavior(props.entry);
        handleSaveBehavior(saveAsNew, !saveAsNew);
        break;
      }
      case ControlBarAction.SaveMetric: {
        const saveAsNew = isUnsavedMetric(props.entry);
        handleSaveMetricWrapper(saveAsNew);
        break;
      }
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      default:
        props.onControlSelect(value);
    }
  };

  const handleCreateInline = (menuType: MenuType) => {
    setIsCreatingInline(true);
    props.onEntryRowCreate(
      {
        type: SelectorTypeDefinitions.Metrics,
        event: null,
        filters: [],
      },
      menuType,
    );
  };

  const handleSelectMultiMetrics = (item: MultiMetricsSectionItem) => {
    props.onEntryRowSelect(
      {
        type: SelectorTypeDefinitions.Metrics,
        event: null,
        filters: [],
      },
      props.entry,
      item,
      props.index,
    );
  };

  const handleSelectSavedBehavior = (
    behavior: Behavior,
    {isSaveExisting = false, isExpanded = false}: {isSaveExisting?: boolean; isExpanded?: boolean} = {},
  ) => {
    props.updateEntry(
      isSaveExisting ? Action.SaveBehavior : Action.Edit,
      {
        ...props.entry,
        isExpanded,
        behavior: getMetricEntryBehavior({
          ...behavior.definition.behavior,
          type: behavior.type,
          name: behavior.name,
          id: behavior.id,
        } as MetricBehavior),
        hasUnsavedChanges: false,
      },
      {
        requery: true,
      },
    );
  };

  const handleSaveBehavior = async (saveAsNew = false, previouslyExisting = false) => {
    const {behavior} = props.entry;
    const {isCreating, results} = await saveBehavior({
      behavior,
      behaviors,
      i18n,
      currentProject,
      saveAsNew,
      previouslyExisting,
    });
    if (!results) {
      return;
    }

    const newBehavior = Object.values(results)[0];
    if (isCreating) {
      track(TrackingEvent.SaveBehavior, {
        'Save as New': saveAsNew,
        ...getTrackingProps(props.entry),
      });
    } else {
      track(TrackingEvent.SaveBehaviorChanges, {...getTrackingProps(props.entry)});
    }
    handleSelectSavedBehavior(newBehavior, {isSaveExisting: !saveAsNew});
  };

  const handleSaveMetricWrapper = async (saveAsNew = false) => {
    return handleSaveMetric(
      saveAsNew,
      hasFirstClassMetrics,
      props.entry,
      props.updateEntry,
      currentProject,
      metrics,
      i18n,
    );
  };

  const handleReset = () => {
    if (isFirstClassMetric) {
      track(TrackingEvent.ResetBehavior, {...getTrackingProps(props.entry)});
      const metric = metrics.getOne(props.entry.id).value;
      handleSelectSavedMetric(metric, props.updateEntry, {isExpanded: true, isSaveExisting: true});
    } else if (isSavedBehavior(props.entry)) {
      track(TrackingEvent.ResetBehavior, {...getTrackingProps(props.entry)});
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      handleSelectSavedBehavior(behavior, {isExpanded: true});
    }
  };

  const handleDeleteEntry = () => {
    if (props.sectionProps.reportName === ReportName.Funnels) {
      // reset to default state if it's app/funnel
      props.updateEntry(Action.Edit, getNewEntry({isExpanded: true}));
      return;
    }
    props.removeEntry();
  };

  const [propertyDisplayOptions, setPropertyDisplayOptions] = useState<BehaviorMetricDisplay>();

  const updateProperty = () => {
    const property = props.entry.measurement?.property;
    if (hasMetricUnits && property) {
      const customPropertyId = `customPropertyId` in property ? property.customPropertyId : null;
      if (customPropertyId) {
        setPropertyDisplayOptions(customPropertiesStore.value[customPropertyId]?.displayOptions);
      } else {
        const propertyValue = isArray(property.value) ? property.value[0] : property.value;
        const propertyDefinition = eventPropertyDefinitionsStore.value[propertyValue];

        setPropertyDisplayOptions(propertyDefinition?.displayOptions);
      }
    } else {
      setPropertyDisplayOptions({});
    }
  };
  useEffect(() => {
    updateProperty();
  }, [props.entry.measurement]);

  const suffixRenderer = () => {
    const metricButton = hasMetricUnits ? (
      <MetricUnitsButton
        insideFooter
        entry={props.entry}
        propertyDisplay={propertyDisplayOptions}
        updateOpen={() => {
          setMetricsUnitOpen((x) => !x);
          props.onMetricUnitSelectorDropdownStateChanged(!props.isMetricUnitSelectorDropdownOpen);
        }}
      />
    ) : null;

    return (
      <>
        {props.entry.isExpanded && (
          <>
            <BehaviorBlock
              {...props}
              measurementBlock={
                hasFirstClassMetrics ? <MeasurementBlock {...props} metricUnitsButton={metricButton} /> : undefined
              }
            />
            {(() => {
              const editState = getEditState(props.entry.type === TopLevelMetricType.Metric);
              if (editState.showEditControls) {
                return (
                  <EditControlsBlock
                    entry={props.entry}
                    handleReset={handleReset}
                    handleSaveBehavior={handleSaveBehavior}
                    handleSaveMetric={handleSaveMetricWrapper}
                    editableState={editState}
                  />
                );
              } else if (getUpsellInfo(currentProject.get().value, ModalTrigger.METRICS)) {
                return (
                  <div className='mp-metric-entry-edit-controls'>
                    <ButtonReact
                      className='upsell-button'
                      theme={ButtonTheme.Secondary}
                      icon={`brand-upgrade`}
                      onClick={() => promptInProductUpsellModal(ModalTrigger.METRICS)}
                    >
                      Save Metric
                    </ButtonReact>
                  </div>
                );
              }
              return null;
            })()}
          </>
        )}
        {!hasFirstClassMetrics && <MeasurementBlock {...props} metricUnitsButton={metricButton} />}
        {hasMetricUnits && (
          <MetricUnitsSelectorDropdown
            callingLocation={CallingLocation.Metric}
            chartType={props.currentChartType}
            entry={props.entry}
            propertyDisplay={propertyDisplayOptions}
            setEntry={(entry) => props.updateEntry(Action.Edit, entry as Entry, {requery: false})}
            open={props.isMetricUnitSelectorDropdownOpen && metricsUnitOpen}
            setOpen={(open) => {
              props.onMetricUnitSelectorDropdownStateChanged(open);
              setMetricsUnitOpen(open);
            }}
          />
        )}
      </>
    );
  };

  const actionsRenderer = () => {
    return (
      <ButtonReact
        className='expand-control'
        icon={props.entry.isExpanded ? `collapse-diagonal` : `expand`}
        theme={ButtonTheme.Tertiary}
        size={ButtonSize.Medium}
        tooltip={{
          text: props.entry.isExpanded ? t(`query-builder.actions.collapse`) : t(`query-builder.actions.expand`),
        }}
        onClick={() => toggleExpandedRow(props)}
      />
    );
  };

  const selectorRenderer = () => {
    let metricName: string = isCreatingInline
      ? t(`query-builder.funnels.label-new`)
      : t(`query-builder.funnels.label-select`);
    let isSaved =
      hasFirstClassMetrics && props.entry.type === TopLevelMetricType.Metric
        ? isSavedMetric(props.entry)
        : isSavedBehavior(props.entry);
    if (isSaved) {
      metricName = props.entry.name || props.entry.behavior.name || metricName;
      const behavior = behaviors.getOne(props.entry.behavior.id).value;
      isSaved = behavior ? !isModifiedEntryBehavior({behavior, entryBehavior: props.entry.behavior}) : false;
    } else {
      metricName = props.entry.behavior.renamed || props.entry.behavior.name || metricName;
    }

    if (!canSaveForPlan(currentProject.get().value) && !hasSavedFunnelBehaviors({behaviors})) {
      return (
        <div className='mp-metric-entry-header-name' title={metricName}>
          <span className='metric-name'>{metricName}</span>
        </div>
      );
    }

    return (
      <div className={`mp-metric-entry-header-selector ${isSaved ? `is-saved` : ``}`}>
        {isNewMetricMenuEnabled && props.sectionProps.reportName === ReportName.Insights ? (
          <RowSelector
            {...props}
            contextPanelParentElement={entryEl}
            isPlaceholder={isPlaceholder}
            selected={{
              menuType: MenuType.Funnel,
              value: {
                id: props.entry.behavior.id,
                name: metricName,
              },
            }}
            selectorType={SelectorTypeDefinitions.Metrics}
            onSelect={(sectionItem: MultiMetricsSectionItem) => handleSelectMultiMetrics(sectionItem)}
            onCreate={(ev: {detail: {item: MenuType}}) => handleCreateInline(ev.detail.item)}
          />
        ) : (
          <RowSelector
            {...props}
            contextPanelParentElement={entryEl}
            isPlaceholder={isPlaceholder}
            selected={{
              id: props.entry.behavior.id,
              name: metricName,
            }}
            queryBlockConfig={{
              type: `behaviors`,
              metricType: MetricType.Funnel,
            }}
            onSelect={(selection: {item: Behavior}) => {
              track(TrackingEvent.SelectBehavior, {
                ...getTrackingProps(props.entry),
              });
              handleSelectSavedBehavior(selection.item);
            }}
            onCreate={() => handleCreateInline(MenuType.Funnel)}
          />
        )}
      </div>
    );
  };

  const {saveDisabledMessage} = getEditState();
  const saveMetricDisabledMessage = getEditState(true).saveDisabledMessage;
  return (
    <>
      <BaseEntryRow
        ref={setEntryEl}
        {...props}
        showLabels={props.sectionProps.reportName === ReportName.Insights}
        showDefaultMetricUnitSelector={false}
        extraEntryClass={{
          'is-metric': true,
        }}
        renderSelector={selectorRenderer}
        renderSuffix={suffixRenderer}
        renderActions={actionsRenderer}
        isPlaceholder={false}
        onControlSelect={handleControlSelect}
        onDeleteEntry={handleDeleteEntry}
        canAlwaysDelete
        overflowControls={[
          ...props.getCommonOverflowControls(),
          ControlBarActionItems[ControlBarAction.Rename],
          {
            ...ControlBarActionItems[ControlBarAction.Save],
            ...(saveDisabledMessage
              ? {
                  isDisabled: true,
                  tooltip: {
                    text: saveDisabledMessage,
                  },
                }
              : {}),
          },
          ...(hasFirstClassMetrics
            ? [
                {
                  ...ControlBarActionItems[ControlBarAction.SaveMetric],
                  ...(saveMetricDisabledMessage
                    ? {
                        isDisabled: true,
                        tooltip: {
                          text: saveMetricDisabledMessage,
                        },
                      }
                    : {}),
                },
              ]
            : []),
          ...(props.sectionProps.reportName === ReportName.Insights
            ? [
                ControlBarActionItems[ControlBarAction.Delete],
                ControlBarActionItems[ControlBarAction.Duplicate],
                props.sectionProps.enableHideEntry &&
                  ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
              ]
            : []),
        ]}
      />
    </>
  );
}

function BehaviorBlock(props: EntryRowProps<MetricEntry>) {
  const currentProject = useCurrentProject().get().value;
  const behaviors = useBehaviors();
  const {t} = useTranslation();

  const hasMetricLevelDataGroups = currentProject?.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics);
  const dataGroupId = getFunnelOrRetentionDataGroupId({currentProject, props});
  const isAnyOrder = isAnyOrderFunnel(props.entry.behavior.funnelOrder);
  const exclusionSteps = props.entry.behavior.exclusions || [];

  const convertBehaviorToEntry = (
    behavior: MetricEntryBehavior,
    {skipEntryIndexToggle = false}: {skipEntryIndexToggle?: boolean} = {},
  ): QueryEventEntry => {
    return {
      ...(skipEntryIndexToggle ? {} : {entryIndexLabelToggle: behavior.funnelOrder === FunnelOrder.Any ? true : false}),
      event: {
        custom: behavior.type === MetricType.CustomEvent,
        id: behavior.id,
        label: renameEvent(behavior.name) || ``,
        renamed: behavior.renamed,
        value: behavior.name,
      },
      filters: behavior.filters,
      filtersOperator: behavior.filtersDeterminer === `any` ? FiltersOperator.Or : FiltersOperator.And,
      type: ResourceType.Event,
      ...(hasMetricLevelDataGroups ? {dataGroupId} : {}),
    };
  };

  const convertEventToBehavior = (
    event: EventItem,
    entry: QueryEventEntry,
    {skipFunnelOrder = false}: {skipFunnelOrder?: boolean} = {},
  ): MetricEntryBehavior => {
    return {
      id: event.custom ? event.id : null,
      type: event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: event.value,
      renamed: event.renamed,
      filters: entry.filters,
      filtersDeterminer: entry.filtersOperator === FiltersOperator.Or ? `any` : `all`,
      filtersOperator: entry.filtersOperator,
      ...(skipFunnelOrder ? {} : {funnelOrder: entry.entryIndexLabelToggle ? FunnelOrder.Any : FunnelOrder.Loose}),
    };
  };

  const getEntries = () => {
    const entries = props.entry.behavior.behaviors.map((behavior) => {
      const entryIndexLabelToggle = behavior.funnelOrder === FunnelOrder.Any ? true : false;
      // handle event comparisons
      if (`behaviors` in behavior) {
        const eventComparisonBehaviors = behavior[`behaviors`];
        const events = eventComparisonBehaviors.map((beh) => convertBehaviorToEntry(beh, {skipEntryIndexToggle: true}));
        const newEntry: QueryGroupedEntry = {
          entryIndexLabelToggle,
          event: events,
          filters: [],
          type: SelectorTypeDefinitions.GroupedEvent,
        };
        return newEntry;
      } else {
        return convertBehaviorToEntry(behavior);
      }
    });
    return entries as QuerySuperEntry[];
  };

  const handleEntryUpdate = (updates: EntrySectionUpdates<QueryEventEntry>) => {
    const {entries} = updates;
    const allBehaviors: MetricEntryBehavior[] = [];

    for (const entry of entries) {
      const groupedEntry = isGroupedEntry(entry);
      if (!groupedEntry) {
        const event = entry.event;
        allBehaviors.push(convertEventToBehavior(event, entry));
      } else {
        const newBehaviors = [];
        const event = (entry as QueryGroupedEntry).event;
        for (const subEvent of event) {
          const subEv = subEvent.event;
          newBehaviors.push(convertEventToBehavior(subEv, subEvent, {skipFunnelOrder: true}));
        }
        allBehaviors.push({
          behaviors: newBehaviors,
          funnelOrder: (entry as QueryGroupedEntry).entryIndexLabelToggle ? FunnelOrder.Any : FunnelOrder.Loose,
        } as MetricEntryBehavior);
      }
    }

    // Reset stepIndex if invalid given behaviors length
    const stepIndex =
      props.entry.measurement.stepIndex >= allBehaviors.length ? null : props.entry.measurement.stepIndex;

    const entry: MetricEntry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior: {
        ...props.entry.behavior,
        behaviors: allBehaviors,
      },
      measurement: {
        ...props.entry.measurement,
        stepIndex,
      },
    };

    // need to pass getEntries(true) because we maybe editing the stagingRow
    // After drag and drop, index can change
    const index = props.getEntries(true).findIndex((el) => el.entryKey === entry.entryKey);
    props.updateEntry(Action.Edit, entry, {index});
  };

  const handleFunnelsConversionCriterialChanged = (options: QueryOptionsSelection) => {
    const {funnelOrder, holdingPropertyConstant, conversionWindow} = options;
    const behavior = {...props.entry.behavior};

    if (options.exclusionSteps) {
      behavior.exclusions = options.exclusionSteps.map((step) => {
        const subBehaviors = behavior.behaviors;
        return mapEventEntryToBookmarkExclusionStep(subBehaviors.length, step);
      });
    }
    if (funnelOrder) {
      behavior.funnelOrder = funnelOrder;
      behavior.behaviors = behavior.behaviors.map((subBehavior, i) => ({
        ...subBehavior,
        funnelOrder: i === 0 ? FunnelOrder.Loose : funnelOrder,
      }));
    }
    if (holdingPropertyConstant) {
      behavior.aggregateBy = holdingPropertyConstant;
    }
    if (conversionWindow) {
      behavior.conversionWindowDuration = conversionWindow.value;
      behavior.conversionWindowUnit = conversionWindow.unit;
    }

    const entry = {
      ...props.entry,
      hasUnsavedChanges: true,
      behavior,
    };
    props.updateEntry(Action.Edit, entry);
  };

  const entries = getEntries();

  const isPlaceholder = helpers.isPlaceholder(props);

  return (
    <div className='mp-metric-entry-behavior' key='behavior-block'>
      <QueryEntrySectionReact
        key='funnelEntries'
        elref='metricEntry'
        {...props.nestedSectionParams}
        autoactivatePlaceholders={
          props.sectionProps.reportName !== ReportName.Insights
            ? false
            : isPlaceholder || !hasSavedFunnelBehaviors({behaviors})
        }
        allowCohortFilter={false}
        groupEventBehavior={GroupEventBehaviors.Nest}
        addEntryLabel={t(`query-builder.select-step`)}
        addEntryButtonLabel={t(`query-builder.add-step`)}
        addEntryButtonTooltipText={t(`query-builder.add-step-tooltip`, {
          link: REPORT_BASICS_LINKS[ReportName.Funnels],
        })}
        minEntries={2}
        measurementBlock={props.measurementBlock}
        emptyPlaceholder={{
          entryIndexLabelToggle: props.entry.behavior.entryIndexLabelToggle,
          event: {
            custom: false,
            id: null,
            label: ``,
            renamed: null,
            value: null,
          },
          filters: [],
          filtersOperator: FiltersOperator.And,
          type: ResourceType.Event,
        }}
        otherFilterOptions={[]}
        rowDraggable
        rowIndex={props.index}
        rowTypes={[ResourceType.Event]}
        rowLabelType={RowLabelType.Numeric}
        reportName={ReportName.Funnels}
        showAllEvents={false}
        showTopEvents={false}
        disableAddGroupedEventReason={
          props.sectionProps.reportName === ReportName.Insights &&
          t(`query-builder.funnels.event-comparison-report-only`)
        }
        {...(isAnyOrder ? {rowIndexToggleable: true, rowIndexToggleIcon: `any-order`} : null)}
        entries={entries}
        onUpdate={handleEntryUpdate}
      />

      <div className='criteria'>
        <ConversionQueryOptionsReact
          eventNames={uniq(props.entry.behavior.behaviors.map((behavior) => behavior.name))}
          projectId={props.sectionProps.projectId}
          hideCountSelector
          workspaceId={props.sectionProps.workspaceId}
          selected={{
            conversionWindow: {
              unit: props.entry.behavior.conversionWindowUnit,
              value: props.entry.behavior.conversionWindowDuration,
            },
            countType: props.entry.measurement.aggregationOperator as any,
            holdingPropertyConstant: props.entry.behavior.aggregateBy,
            funnelOrder: props.entry.behavior.funnelOrder,
          }}
          showSessions
          builderParams={{
            entries: entries as QueryEventEntry[],
            exclusionSteps: exclusionSteps.map((exclusion) =>
              mapBookmarkExclusionStepToEventEntry(entries.length, exclusion),
            ),
            dataGroupsEnabled: currentProject?.canUseCohorts(),
            dataGroupId,
            projectHasPeople: currentProject?.hasIntegratedEngage,
            projectHasCohorts: currentProject?.canUseCohorts(),
            projectId: props.sectionProps.projectId,
            workspaceId: props.sectionProps.workspaceId,
            trackName: props.sectionProps.trackName,
            projectAsOfDate: currentProject?.projectAsOfDate,
            entryRowLabelType: RowLabelType.None,
          }}
          maxHoldingPropertyConstant={MAX_HOLDING_PROPERTY_CONSTANT}
          conversionWindowUnits={CONVERSION_WINDOW_UNITS}
          conversionWindowSizeLimits={CONVERSION_WINDOW_SIZE_LIMITS}
          disableAnyOrderMessage={
            DateRange.hasSubDayGranularity(props.sectionProps?.dateRange)
              ? t(`reports.chart-types.disabled-reasons.sub-day-granularity-with-any-order`)
              : ``
          }
          hideAnyOrder={false}
          hideConversionWindow={false}
          enableStickyHeader={false}
          onChangeQueryOption={handleFunnelsConversionCriterialChanged}
        />
      </div>
    </div>
  );
}

function MeasurementBlock(props: EntryRowProps<MetricEntry> & {metricUnitsButton?: React.ReactNode}) {
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {t, print} = useTranslation();
  const currentProject = useCurrentProject().get().value;
  const dataGroupStore = useDataGroups();

  const showMetricDataGroupSelect = canShowMetricDataGroupSelect({currentProject, dataGroupStore});
  const dataGroupId = getFunnelOrRetentionDataGroupId({currentProject, props});

  const handleFunnelsCountBySelected = (countBy: CountBy) => {
    const {aggregationOperator, property, countUsersOnceType} = countBy;

    let conversionWindowDuration = props.entry.behavior.conversionWindowDuration;
    let conversionWindowUnit = props.entry.behavior.conversionWindowUnit;
    let stepIndex = props.entry.measurement.stepIndex;
    let hasUnsavedChanges = false;
    if (
      [FUNNELS_MATH_TYPES.SESSION, FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION].includes(
        aggregationOperator as FUNNELS_MATH_TYPES,
      )
    ) {
      conversionWindowDuration = 1;
      conversionWindowUnit = ConversionWindowUnit.Session;
      hasUnsavedChanges = true;
    }
    // if stepIndex is 0(Entering Funnel), set it to null(All Steps) if someone selects conversion rate measurement
    if (
      [
        FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
        FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
      ].includes(aggregationOperator as FUNNELS_MATH_TYPES) &&
      stepIndex === 0
    ) {
      stepIndex = null;
    }

    const entry = {
      ...props.entry,
      ...(hasUnsavedChanges ? {hasUnsavedChanges} : {}),
      behavior: {
        ...props.entry.behavior,
        conversionWindowDuration,
        conversionWindowUnit,
      },
      measurement: {
        ...props.entry.measurement,
        stepIndex,
        aggregationOperator,
        property,
        countUsersOnceType: getCountUsersOnceTypeFromEntrySelection({
          selectedCountUsersOnceType: countUsersOnceType,
          metricType: MetricType.Funnel,
          multiAttribution: props.entry.measurement.multiAttribution,
        }),
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleStepSelection = (ev: ChangeEvent<RangeSelection>) => {
    const {value} = ev.detail.item;

    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        stepIndex: value?.isEntireRange ? null : value?.range?.from,
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const handleAttributionSelection = ({detail}: CustomEvent) => {
    const entry = {
      ...props.entry,
      measurement: {
        ...props.entry.measurement,
        attributionSelection: detail.item.value,
      },
    };
    props.updateEntry(Action.Edit, entry);
  };

  const stepSelectionSections: Section[] = [
    {
      icon: `info-xs`,
      tooltip: {text: t(`query-builder.step-selection.tooltip`)},
      label: t(`query-builder.step-selection.title`),
      items: getStepSelectionOptions({
        entry: props.entry,
        chartType: props.sectionProps.chartType,
      }),
    },
  ];

  const stepSelectionLabel = getStepSelectionLabel({entry: props.entry, chartType: props.sectionProps.chartType});
  const breakdownAttributionLabel = props.sectionProps.breakdownAttributionLabeler
    ? print(
        props.sectionProps.breakdownAttributionLabeler(
          print(getFunnelMetricAttributionItems(props.entry).find((entry) => entry.isSelected)?.label) || `...`,
        ),
      )
    : `...`;
  const multiAttribution = getMultiAttributionFromEntry(props.entry);
  const isMultiAttributionOpen = props.activePart === EntryPart.MultiAttribution;

  return (
    <div
      className={clsx('mp-metric-entry-measurement', {'has-dg-select': showMetricDataGroupSelect})}
      key='measurement-block'
    >
      <div className='mp-metric-entry-measurement-inner'>
        {props.metricUnitsButton}
        {showMetricDataGroupSelect && (
          <DataGroupSelect
            selectedDataGroup={dataGroupId}
            onSelect={(id) => {
              const entry = {
                ...props.entry,
                measurement: {...props.entry.measurement, dataGroupId: id},
              };
              props.updateEntry(Action.Edit, entry);
            }}
          />
        )}
        <CountQueryBlock
          className='count-menu'
          elref='countBlock'
          metricType={MetricType.Funnel}
          dataGroupId={dataGroupId}
          projectId={props.sectionProps.projectId}
          countUsersOnceState={
            props.sectionProps.canUseMultiAttribution
              ? CountUsersOnceState.Required
              : props.sectionProps.countUsersOnceState
          }
          eventNames={props.entry.behavior.behaviors.map((behavior) => behavior.name)}
          selected={{
            countType: (props.entry.measurement.property?.value === FUNNELS_TIME_TO_CONVERSION.value
              ? FunnelsCountType.General
              : [
                    FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
                    FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
                    FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
                  ].includes(props.entry.measurement.aggregationOperator as FUNNELS_MATH_TYPES)
                ? FunnelsCountType.ConversionRate
                : props.entry.measurement.aggregationOperator) as CountType,
            aggregationOperator: props.entry.measurement.aggregationOperator,
            property: props.entry.measurement.property,
            countUsersOnceType: props.entry.measurement.countUsersOnceType,
          }}
          trackName='funnels'
          userId={props.sectionProps.userId}
          workspaceId={props.sectionProps.workspaceId}
          onSelect={handleFunnelsCountBySelected}
          includeCustomProperties={props.sectionProps.includeCustomProperties}
          showBorrowedProperties={props.sectionProps.showBorrowedProperties}
        />
        <SelectReact
          alwaysShowCaret
          label={stepSelectionLabel}
          theme={SelectTheme.Tertiary}
          size={ButtonSize.Small}
          menuAlignment={Directions.Right}
          menuWidth={184}
          disableFirstItemPadding
          elref='advancedCountMenuSelect'
          sections={stepSelectionSections}
          onChange={handleStepSelection}
        />
      </div>
      {!props.sectionProps.canUseMultiAttribution && props.sectionProps.canUseBreakdownAttribution && (
        <div className='mp-metric-entry-measurement-inner'>
          <SelectReact
            elref='attributionSelect'
            alwaysShowCaret
            sections={[{items: getFunnelMetricAttributionItems(props.entry)}]}
            label={breakdownAttributionLabel}
            selectedOption=''
            size={ButtonSize.Small}
            theme={SelectTheme.Tertiary}
            menuWidth={200}
            tabIndex={0}
            experimentalPositioning
            onChange={handleAttributionSelection}
          />
        </div>
      )}
      {props.sectionProps.canUseMultiAttribution && multiAttribution && (
        <div className='mp-metric-entry-measurement-inner'>
          <SelectReact
            className='subitem-select multi-attribution-type'
            elref='multiAttributionSelector'
            alwaysShowCaret
            label={print(getMultiAttributionLabel(props.entry, isMultiAttributionOpen))}
            title={isMultiAttributionOpen ? `` : print(getMultiAttributionTitle(props.entry))}
            open={isMultiAttributionOpen}
            theme={SelectTheme.Tertiary}
            size={ButtonSize.Small}
            onMenuChange={(ev) => props.setActivePart(EntryPart.MultiAttribution, ev.detail.isOpen ? `open` : `close`)}
            tooltip={{
              text: `${print(getMultiAttributionTitle(props.entry))} ${breakdownAttributionLabel}.`,
              size: DisplaySize.Medium,
            }}
          >
            <div slot='body'>
              {isMultiAttributionOpen && (
                <MultiAttributionMenu
                  onCancel={() => props.setActivePart(EntryPart.MultiAttribution, `close`)}
                  onChange={(updatedMultiAttribution) =>
                    props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, updatedMultiAttribution))
                  }
                  onDelete={() => props.updateEntry(Action.Edit, getUpdatedMultiAttribution(props.entry, null))}
                  isSupportedMultiAttributionType={(type) =>
                    isSupportedMultiAttributionTypeForEntry({
                      entry: props.entry,
                      multiAttributionType: type,
                      metricsResource: metrics,
                      behaviorsResource: behaviors,
                    })
                  }
                  showMultiAttributionType={(type) =>
                    [
                      MultiAttributionType.LastTouch,
                      MultiAttributionType.FirstTouch,
                      MultiAttributionType.Participation,
                    ].includes(type)
                  }
                  metricIndex={props.index}
                  multiAttribution={multiAttribution || DefaultMultiAttribution}
                  projectIsFreePlan={props.sectionProps.projectIsFreePlan}
                />
              )}
            </div>
          </SelectReact>
        </div>
      )}
    </div>
  );
}

function EditControlsBlock(props: {
  entry: MetricEntry;
  handleReset: () => void;
  handleSaveBehavior: (saveAsNew: boolean, previouslyExisting: boolean) => void;
  handleSaveMetric?: (saveAsNew: boolean, previouslyExisting: boolean) => void;
  editableState: EditableState;
}) {
  const hasFirstClassMetrics = currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.FirstClassMetrics);
  const isFirstClassMetric = hasFirstClassMetrics && isSavedMetric(props.entry);
  const {canEdit, canSave, isSaved, isSaving, saveDisabledMessage} = props.editableState;

  return (
    <div className='mp-metric-entry-edit-controls'>
      <EditControls
        resetTooltip={({t}) => t(`query-builder.edit-controls.reset-tooltip`)}
        isSaved={isSaved}
        isSaving={isSaving}
        canSave={canSave}
        canReset={canSave && (isSavedBehavior(props.entry) || isFirstClassMetric)}
        canEdit={canEdit}
        saveDisabledMessage={saveDisabledMessage}
        entityType={({t}) => t(`query-builder.metrics.behavior`)}
        onReset={props.handleReset}
        onSave={() => props.handleSaveBehavior(false, isSavedBehavior(props.entry))}
        onSaveMetric={() => props.handleSaveMetric(false, isSavedMetric(props.entry))}
        onClone={() => props.handleSaveBehavior(true, isSavedBehavior(props.entry))}
      />
    </div>
  );
}
